import Phaser from "phaser";
import { FallingEvent } from "../entities/FallingEvent";
import { TypedGameBus } from "../events/typed-bus";
import { DesktopInputController } from "../input/DesktopInputController";
import { applyLetter, normalizeWord } from "../domain/normalization";
import {
  createInitialState,
  discoveriesFor,
  expireEvent,
  registerKey,
  registerSpawn,
  resolveEvent,
  selectTarget,
} from "../domain/game-state";
import { GAME_RULES, phaseForOrdinal } from "../domain/rules";
import { spawnGate } from "../domain/spawn-policy";
import {
  buildSessionSequence,
  createSeededRandom,
  scheduleAdaptiveRecoveries,
  type SessionRandom,
} from "../domain/session-plan";
import type {
  ActiveEventSnapshot,
  GameState,
  InputIntent,
  LevelDefinition,
  RuntimeGameEventDefinition,
} from "../domain/types";

interface GameSceneData {
  level: LevelDefinition;
  coarsePointer: boolean;
  attemptIndex: number;
  randomSeed: number;
  bus: TypedGameBus;
}

interface RuntimeEvent {
  instanceId: string;
  definition: RuntimeGameEventDefinition;
  ordinal: number;
  anchor: number;
  trajectoryOffset: number;
  startedAt: number;
  durationMs: number;
  typedLength: number;
  view: FallingEvent;
}

export class GameScene extends Phaser.Scene {
  private level!: LevelDefinition;
  private bus!: TypedGameBus;
  private coarsePointer = false;
  private attemptIndex = 0;
  private random!: SessionRandom;
  private sessionDefinitions: RuntimeGameEventDefinition[] = [];
  private recoveryScheduled = false;
  private reducedMotion = false;
  private state!: GameState;
  private sessionTime = 0;
  private sequenceCursor = 0;
  private phaseIndex = 0;
  private phaseReadyAt = 0;
  private nextSpawnAt = 0;
  private readonly active = new Map<string, RuntimeEvent>();
  private readonly inputQueue: InputIntent[] = [];
  private desktopInput: DesktopInputController | null = null;
  private ending = false;
  private background!: Phaser.GameObjects.Graphics;
  private midground!: Phaser.GameObjects.Graphics;
  private foreground!: Phaser.GameObjects.Graphics;

  constructor() {
    super("GameScene");
  }

  init(data: GameSceneData): void {
    this.level = data.level;
    this.bus = data.bus;
    this.coarsePointer = data.coarsePointer;
    this.attemptIndex = data.attemptIndex;
    this.random = createSeededRandom(data.randomSeed);
    this.sessionDefinitions = buildSessionSequence(
      data.level,
      data.attemptIndex,
    );
    this.recoveryScheduled = false;
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.state = createInitialState(data.level.id);
    this.sessionTime = 0;
    this.sequenceCursor = 0;
    this.phaseIndex = 0;
    this.phaseReadyAt = 0;
    this.nextSpawnAt = 0;
    this.ending = false;
    this.active.clear();
    this.inputQueue.length = 0;
  }

  create(): void {
    this.background = this.add.graphics().setDepth(-30);
    this.midground = this.add.graphics().setDepth(-20);
    this.foreground = this.add.graphics().setDepth(-10);
    this.drawTheme();
    this.scale.on("resize", this.handleResize, this);
    if (!this.coarsePointer)
      this.desktopInput = new DesktopInputController((intent) =>
        this.queueIntent(intent),
      );
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.dispose, this);
    this.emitState();
  }

  update(_time: number, delta: number): void {
    if (this.state.status !== "playing" || this.ending) return;
    this.sessionTime += Math.min(delta, 100);

    while (this.inputQueue.length > 0) {
      const intent = this.inputQueue.shift();
      if (intent) this.processIntent(intent);
    }
    if (this.ending) return;

    const expired: RuntimeEvent[] = [];
    this.active.forEach((event) => {
      const progress = Phaser.Math.Clamp(
        (this.sessionTime - event.startedAt) / event.durationMs,
        0,
        1,
      );
      event.view.setResponsiveTrajectory(
        event.anchor,
        event.trajectoryOffset,
        progress,
        this.reducedMotion,
      );
      if (progress >= 1) expired.push(event);
    });
    this.updateDioramaParallax();
    expired
      .sort((a, b) => a.ordinal - b.ordinal)
      .forEach((event) => this.expire(event));
    if (this.ending) return;

    this.advancePhaseIfReady();
    this.attemptSpawn();
    this.emitState();
  }

  queueIntent(intent: InputIntent): void {
    if (this.state.status === "playing" && !this.ending)
      this.inputQueue.push(intent);
  }

  pauseSession(): void {
    if (this.state.status !== "playing") return;
    this.state = { ...this.state, status: "paused" };
    this.emitState();
    this.scene.pause();
  }

  resumeSession(): void {
    if (this.state.status !== "paused") return;
    this.state = { ...this.state, status: "playing" };
    this.scene.resume();
    this.emitState();
  }

  debugResolveFirst(): boolean {
    let event = [...this.active.values()].sort(
      (a, b) => a.ordinal - b.ordinal,
    )[0];
    if (!event) {
      this.debugAdvanceClock();
      event = [...this.active.values()].sort(
        (a, b) => a.ordinal - b.ordinal,
      )[0];
    }
    if (!event) return false;
    this.complete(event);
    this.debugAdvanceClock();
    return true;
  }

  debugExpireFirst(): boolean {
    const event = [...this.active.values()].sort(
      (a, b) => a.ordinal - b.ordinal,
    )[0];
    if (!event) return false;
    this.expire(event);
    this.debugAdvanceClock();
    return true;
  }

  getStateSnapshot(): GameState {
    return { ...this.state, discoveryKeys: [...this.state.discoveryKeys] };
  }

  getActiveSnapshots(): ActiveEventSnapshot[] {
    return this.createActiveSnapshots();
  }

  debugSetResources(hearts: 1 | 2 | 3, shieldActive = false): void {
    if (!import.meta.env.DEV || this.ending) return;
    this.state = { ...this.state, hearts, shieldActive };
    this.emitState();
  }

  private processIntent(intent: InputIntent): void {
    this.processLetter(intent.value);
  }

  private processLetter(letter: string): void {
    let target = this.state.targetInstanceId
      ? this.active.get(this.state.targetInstanceId)
      : undefined;
    if (!target) {
      const normalizedLetter = normalizeWord(letter);
      target = [...this.active.values()]
        .filter(
          (event) =>
            normalizeWord(event.definition.word)[0] === normalizedLetter,
        )
        .sort((a, b) => {
          const remainingA = a.durationMs - (this.sessionTime - a.startedAt);
          const remainingB = b.durationMs - (this.sessionTime - b.startedAt);
          return remainingA - remainingB || a.ordinal - b.ordinal;
        })[0];
      if (target) this.acquireTarget(target.instanceId);
    }

    if (!target) {
      this.state = registerKey(this.state, false);
      this.bus.emit("audio", { cue: "typo" });
      this.bus.emit("visual", { cue: "typo" });
      return;
    }

    const result = applyLetter(
      target.definition.word,
      target.typedLength,
      letter,
    );
    this.state = registerKey(this.state, result.correct);
    if (!result.correct) {
      this.bus.emit("audio", { cue: "typo" });
      this.bus.emit("visual", { cue: "typo" });
      return;
    }

    target.typedLength = result.typedLength;
    const progress =
      result.typedLength / normalizeWord(target.definition.word).length;
    target.view.setTypedProgress(progress);
    target.view.pop();
    this.bus.emit("audio", { cue: "correct-key", progress });
    if (result.complete) this.complete(target);
  }

  private acquireTarget(instanceId: string): void {
    this.state = selectTarget(this.state, instanceId);
    this.active.forEach(({ view }, id) => view.setSelected(id === instanceId));
    this.bus.emit("audio", { cue: "target" });
    this.emitState();
  }

  private complete(event: RuntimeEvent): void {
    if (!this.active.has(event.instanceId) || this.ending) return;
    this.state = resolveEvent(this.state, event.definition);
    this.bus.emit("audio", { cue: "resolved" });
    const rewardCue = this.rewardCue(event.definition);
    if (rewardCue) this.bus.emit("audio", { cue: rewardCue });
    this.bus.emit("feedback", event.definition.response);
    this.bus.emit("visual", { cue: event.definition.response.visualCue });
    this.removeEvent(event, true);
    if (this.state.status === "won") this.endSession("victory");
    else this.emitState();
  }

  private expire(event: RuntimeEvent): void {
    if (!this.active.has(event.instanceId) || this.ending) return;
    const hadShield = this.state.shieldActive;
    const heartsBefore = this.state.hearts;
    const targeted = this.state.targetInstanceId === event.instanceId;
    this.state = expireEvent(this.state, event.definition);
    if (targeted) this.state = selectTarget(this.state, null);
    if (event.definition.type === "threat") {
      this.bus.emit("audio", { cue: hadShield ? "shield-break" : "damage" });
      this.bus.emit("visual", {
        cue: hadShield ? "shield-break" : "damage",
        critical: true,
      });
    }
    if (
      !this.recoveryScheduled &&
      !hadShield &&
      heartsBefore === 2 &&
      this.state.hearts === 1
    ) {
      this.scheduleRecoveries();
    }
    this.removeEvent(event, false);
    if (this.state.status === "lost") this.endSession("game_over");
    else if (this.state.status === "won") this.endSession("victory");
    else this.emitState();
  }

  private removeEvent(event: RuntimeEvent, resolved: boolean): void {
    this.active.delete(event.instanceId);
    if (resolved && event.definition.id !== "fallecimiento") {
      this.tweens.add({
        targets: event.view,
        alpha: 0,
        scale: 1.15,
        duration: 220,
        onComplete: () => event.view.destroy(),
      });
    } else {
      this.tweens.add({
        targets: event.view,
        alpha: 0,
        duration: 220,
        onComplete: () => event.view.destroy(),
      });
    }
    if (this.state.targetInstanceId === event.instanceId)
      this.state = selectTarget(this.state, null);
    if (this.phaseIndex === 0 && this.sequenceCursor < 6)
      this.nextSpawnAt = this.sessionTime + 350;
  }

  private attemptSpawn(): void {
    const phase = GAME_RULES.phases[this.phaseIndex];
    if (
      !phase ||
      this.sequenceCursor >= phase.end ||
      this.sessionTime < this.phaseReadyAt
    )
      return;
    const definition = this.sessionDefinitions[this.sequenceCursor];
    if (!definition) return;
    if (
      spawnGate({
        ordinal: this.sequenceCursor + 1,
        word: definition.word,
        nowMs: this.sessionTime,
        phaseReadyAtMs: this.phaseReadyAt,
        nextSpawnAtMs: this.nextSpawnAt,
        activeWords: [...this.active.values()].map(
          (event) => event.definition.word,
        ),
      }) !== "ready"
    )
      return;

    const ordinal = this.sequenceCursor + 1;
    const instanceSuffix =
      definition.type === "recovery" ? "recovery" : definition.id;
    const instanceId = `${this.level.id}-${ordinal}-${instanceSuffix}`;
    const anchor =
      GAME_RULES.anchors[this.sequenceCursor % GAME_RULES.anchors.length] ??
      0.5;
    const view = new FallingEvent(
      this,
      instanceId,
      definition.word,
      definition.type,
      definition.basePoints,
    );
    const runtime: RuntimeEvent = {
      instanceId,
      definition,
      ordinal,
      anchor,
      trajectoryOffset:
        [-0.14, 0.12, -0.12, 0.14, -0.1, 0.1][this.sequenceCursor % 6] ?? 0,
      startedAt: this.sessionTime,
      durationMs: phaseForOrdinal(ordinal).durationMs,
      typedLength: 0,
      view,
    };
    view.setResponsiveTrajectory(
      anchor,
      runtime.trajectoryOffset,
      0,
      this.reducedMotion,
    );
    this.active.set(instanceId, runtime);
    this.sequenceCursor += 1;
    this.state = registerSpawn(this.state);
    this.nextSpawnAt =
      this.phaseIndex === 0
        ? Number.POSITIVE_INFINITY
        : this.sessionTime + phase.spawnIntervalMs;
    const spawnCue =
      definition.type === "threat"
        ? "spawn-threat"
        : definition.type === "recovery"
          ? "spawn-recovery"
          : "spawn-opportunity";
    this.bus.emit("audio", { cue: spawnCue });
  }

  private advancePhaseIfReady(): void {
    const phase = GAME_RULES.phases[this.phaseIndex];
    if (!phase || this.state.processedCount < phase.end || this.active.size > 0)
      return;
    if (this.phaseIndex >= GAME_RULES.phases.length - 1) return;
    this.phaseIndex += 1;
    this.phaseReadyAt = this.sessionTime + GAME_RULES.phaseBreakMs;
    this.nextSpawnAt = this.phaseReadyAt;
  }

  private rewardCue(
    event: RuntimeGameEventDefinition,
  ): "shield" | "heal" | "bonus" | "combo" | null {
    switch (event.reward.type) {
      case "shield":
        return "shield";
      case "heal":
        return "heal";
      case "combo_boost":
        return "combo";
      case "bonus_points":
        return "bonus";
      case "none":
        return null;
    }
  }

  private emitState(): void {
    this.bus.emit("state", {
      state: { ...this.state },
      active: this.createActiveSnapshots(),
    });
  }

  private createActiveSnapshots(): ActiveEventSnapshot[] {
    return [...this.active.values()].map((event) => ({
      instanceId: event.instanceId,
      definitionId: event.definition.id,
      word: event.definition.word,
      type: event.definition.type,
      typedLength: event.typedLength,
      progress: Phaser.Math.Clamp(
        (this.sessionTime - event.startedAt) / event.durationMs,
        0,
        1,
      ),
      remainingMs: Math.max(
        0,
        event.durationMs - (this.sessionTime - event.startedAt),
      ),
      spawnOrdinal: event.ordinal,
    }));
  }

  private endSession(reason: "victory" | "game_over"): void {
    if (this.ending) return;
    this.ending = true;
    this.inputQueue.length = 0;
    this.desktopInput?.dispose();
    this.desktopInput = null;
    this.active.forEach(({ view }) => view.destroy());
    this.active.clear();
    this.tweens.killAll();
    this.state = {
      ...this.state,
      status: reason === "victory" ? "won" : "lost",
      targetInstanceId: null,
    };
    this.emitState();
    this.bus.emit("audio", {
      cue: reason === "victory" ? "victory" : "defeat",
    });
    this.bus.emit("ended", {
      result: reason,
      state: { ...this.state },
      discoveries: discoveriesFor(this.state),
    });
  }

  private handleResize(): void {
    this.drawTheme();
  }

  private drawTheme(): void {
    if (!this.background || !this.midground || !this.foreground) return;
    const width = this.scale.width;
    const height = this.scale.height;
    this.background
      .clear()
      .fillStyle(0xf5f6fc)
      .fillRect(0, 0, width, height)
      .fillStyle(0x00adee, 0.055)
      .fillCircle(width * 0.84, height * 0.12, width * 0.24)
      .fillStyle(0x0855c4, 0.035)
      .fillCircle(width * 0.08, height * 0.68, width * 0.2);
    this.midground.clear();
    this.foreground.clear();

    if (this.level.artTheme === "vida") this.drawVidaDiorama(width, height);
    else if (this.level.artTheme === "soat")
      this.drawSoatDiorama(width, height);
    else this.drawRentasDiorama(width, height);
  }

  private drawVidaDiorama(width: number, height: number): void {
    this.midground
      .fillStyle(0x0855c4, 0.06)
      .fillRoundedRect(
        width * 0.16,
        height * 0.62,
        width * 0.14,
        height * 0.12,
        18,
      )
      .fillStyle(0x00adee, 0.07)
      .fillRoundedRect(
        width * 0.67,
        height * 0.31,
        width * 0.13,
        height * 0.11,
        18,
      );
    this.midground.lineStyle(3, 0x00adee, 0.12);
    this.midground.beginPath();
    this.midground.moveTo(width * 0.08, height * 0.9);
    for (let step = 1; step <= 24; step += 1) {
      const t = step / 24;
      const inverse = 1 - t;
      this.midground.lineTo(
        inverse * inverse * width * 0.08 +
          2 * inverse * t * width * 0.5 +
          t * t * width * 0.92,
        inverse * inverse * height * 0.9 +
          2 * inverse * t * height * 0.74 +
          t * t * height * 0.88,
      );
    }
    this.midground.strokePath();

    const baseY = height * 0.9;
    this.foreground
      .fillStyle(0x0855c4, 0.18)
      .fillCircle(width * 0.1, baseY - 34, 13)
      .fillRoundedRect(width * 0.085, baseY - 22, 30, 38, 10)
      .fillStyle(0x00adee, 0.22)
      .fillCircle(width * 0.14, baseY - 25, 10)
      .fillRoundedRect(width * 0.129, baseY - 15, 22, 31, 8);
    this.foreground
      .fillStyle(0xea0c90, 0.16)
      .fillTriangle(
        width * 0.84,
        baseY - 33,
        width * 0.91,
        baseY - 85,
        width * 0.98,
        baseY - 33,
      )
      .fillStyle(0x0855c4, 0.15)
      .fillRoundedRect(width * 0.855, baseY - 33, width * 0.11, 50, 5)
      .fillStyle(0xf5f6fc, 0.9)
      .fillRect(width * 0.9, baseY - 13, width * 0.026, 30);
  }

  private drawSoatDiorama(width: number, height: number): void {
    const horizon = height * 0.58;
    for (let index = 0; index < 8; index += 1) {
      const buildingWidth = width * (0.035 + (index % 3) * 0.014);
      const x = width * (0.04 + index * 0.125);
      const buildingHeight = height * (0.08 + (index % 4) * 0.035);
      this.midground
        .fillStyle(index % 2 === 0 ? 0x0855c4 : 0x00adee, 0.055)
        .fillRoundedRect(
          x,
          horizon - buildingHeight,
          buildingWidth,
          buildingHeight,
          5,
        );
    }
    this.foreground
      .fillStyle(0x0855c4, 0.08)
      .fillTriangle(
        width * 0.12,
        height,
        width * 0.48,
        horizon,
        width * 0.5,
        height,
      )
      .fillTriangle(
        width * 0.5,
        height,
        width * 0.52,
        horizon,
        width * 0.88,
        height,
      );
    this.foreground.lineStyle(3, 0x00adee, 0.14);
    this.foreground.beginPath();
    this.foreground.moveTo(width * 0.5, height);
    this.foreground.lineTo(width * 0.5, horizon);
    this.foreground.strokePath();
    this.foreground
      .fillStyle(0x0855c4, 0.22)
      .fillRoundedRect(width * 0.1, height * 0.86, 58, 24, 8)
      .fillStyle(0x11181c, 0.4)
      .fillCircle(width * 0.115, height * 0.9, 6)
      .fillCircle(width * 0.1 + 45, height * 0.9, 6)
      .fillStyle(0xea0c90, 0.17)
      .fillCircle(width * 0.9, height * 0.79, 18)
      .fillTriangle(
        width * 0.886,
        height * 0.81,
        width * 0.914,
        height * 0.81,
        width * 0.9,
        height * 0.86,
      );
  }

  private drawRentasDiorama(width: number, height: number): void {
    for (let index = 0; index < 5; index += 1) {
      this.midground
        .lineStyle(
          2 + index,
          index % 2 === 0 ? 0x00adee : 0x0855c4,
          0.045 + index * 0.012,
        )
        .strokeCircle(
          width * 0.76,
          height * 0.48,
          width * (0.1 + index * 0.055),
        );
    }
    for (let index = 0; index < 5; index += 1) {
      const stepWidth = width * (0.5 - index * 0.07);
      this.foreground
        .fillStyle(index % 2 === 0 ? 0x0855c4 : 0x00adee, 0.07 + index * 0.018)
        .fillRoundedRect(
          width * 0.48,
          height * (0.92 - index * 0.055),
          stepWidth,
          height * 0.045,
          8,
        );
    }
    this.foreground
      .fillStyle(0x0855c4, 0.2)
      .fillCircle(width * 0.12, height * 0.83, 12)
      .fillRoundedRect(width * 0.106, height * 0.845, 28, 42, 10)
      .fillStyle(0xea0c90, 0.15)
      .fillCircle(width * 0.91, height * 0.68, 26)
      .fillStyle(0xf5f6fc, 0.85)
      .fillCircle(width * 0.91, height * 0.68, 14);
  }

  private updateDioramaParallax(): void {
    if (this.reducedMotion) {
      this.midground.setPosition(0, 0);
      this.foreground.setPosition(0, 0);
      return;
    }
    const wave = Math.sin(this.sessionTime / 2400);
    this.midground.x = wave * 4;
    this.foreground.x = wave * -8;
  }

  private scheduleRecoveries(): void {
    this.recoveryScheduled = true;
    const recovery = this.level.events.find(
      ({ id }) => id === this.level.recoveryEventId,
    );
    if (!recovery) return;
    const result = scheduleAdaptiveRecoveries(
      this.sessionDefinitions,
      this.sequenceCursor,
      recovery,
      this.attemptIndex,
      this.random,
    );
    this.sessionDefinitions = result.sequence;
  }

  private dispose(): void {
    this.desktopInput?.dispose();
    this.desktopInput = null;
    this.scale.off("resize", this.handleResize, this);
    this.active.clear();
    this.inputQueue.length = 0;
  }

  private debugAdvanceClock(): void {
    if (this.ending) return;
    this.nextSpawnAt = this.sessionTime;
    const phase = GAME_RULES.phases[this.phaseIndex];
    if (
      phase &&
      this.state.processedCount >= phase.end &&
      this.active.size === 0
    ) {
      this.phaseReadyAt = this.sessionTime;
      this.advancePhaseIfReady();
      this.phaseReadyAt = this.sessionTime;
      this.nextSpawnAt = this.sessionTime;
    }
    this.attemptSpawn();
    this.emitState();
  }
}
